/* PublicPresenceSection — إعدادات ظهور المحامي في جدول الترتيب والدليل العامّ.
   - مفتاح تَفعيل/إيقاف الظهور (افتراضي: مُتَوقّف).
   - حقول: التَّخصّص، السيرة، رقم الهاتف، المُحافَظة، القَضاء.
   - زرّ "تَغيير الأَفتار" يَفتح CameraAvatarPicker. */
import { useState } from "react";
import { Camera, GlobeHemisphereWest, Check, Phone, MapPin, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import CameraAvatarPicker from "./CameraAvatarPicker";
import BarCardStatus from "./BarCardStatus";
import { updatePublicPresence, uploadAvatar, avatarUrl } from "../../lib/api";
import { userUpdateMe } from "../../lib/userApi";

const GOVERNORATES = [
  "بَغداد", "البَصرة", "نَينَوى", "أَربيل", "كَربلاء", "النَّجَف",
  "ذي قار", "بابل", "الأَنبار", "ديالى", "كركوك", "صلاح الدِّين",
  "واسط", "ميسان", "المُثَنَّى", "القادِسيَّة", "دَهوك", "السُّلَيمانيَّة",
];

const SectionTitle = ({ children }) => (
  <h2 className="text-[12px] font-bold text-[#5A6470] mt-5 mb-2 px-1 uppercase tracking-wider">
    {children}
  </h2>
);

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative w-12 h-7 rounded-full transition-colors ${checked ? "bg-[#22C55E]" : "bg-[#D1D5DB]"}`}
    >
      <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${checked ? "right-0.5" : "right-[22px]"}`} />
    </button>
  );
}

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-[12.5px] font-bold text-[#0A192F] mb-1">
      {label}{required && <span className="text-[#B91C1C]"> *</span>}
    </label>
    {children}
    {hint && <p className="text-[10.5px] text-[#5A6470] mt-1 leading-relaxed">{hint}</p>}
  </div>
);

const inputCls =
  "w-full px-3 py-2.5 rounded-xl border border-[#EAEAEA] bg-white text-[14px] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition";

export default function PublicPresenceSection({ user, onSaved }) {
  const [optIn, setOptIn] = useState(Boolean(user?.leaderboard_opt_in));
  const [name, setName] = useState(user?.name || "");
  const [specialty, setSpecialty] = useState(user?.specialty || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [city, setCity] = useState(user?.city || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [governorate, setGovernorate] = useState(user?.governorate || "");
  const [district, setDistrict] = useState(user?.district || "");
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(false);
  const [avatarVer, setAvatarVer] = useState(0);

  const save = async () => {
    if (busy) return;
    if (!name.trim()) { toast.error("اكتب اسمك الكامِل"); return; }
    if (optIn && !specialty.trim()) { toast.error("اكتب تَخصّصك قبل التَّفعيل"); return; }
    if (optIn && !phone.trim()) { toast.error("اكتب رقم الهاتف قبل التَّفعيل"); return; }
    setBusy(true);
    try {
      // 1) name (in user record) — phone goes via public-presence (single source of truth)
      if (name.trim() !== (user?.name || "")) {
        await userUpdateMe({ name: name.trim() });
      }
      // 2) public-presence fields
      const fresh = await updatePublicPresence({
        leaderboard_opt_in: optIn,
        specialty, bio, city, phone, governorate, district,
      });
      toast.success("تَمّ حفظ بَياناتك");
      onSaved && onSaved(fresh);
    } catch (e) {
      const msg = e?.response?.data?.detail || "تَعذّر الحفظ";
      toast.error(typeof msg === "string" ? msg : "تَعذّر الحفظ");
    } finally {
      setBusy(false);
    }
  };

  const onCaptured = async (blob) => {
    setBusy(true);
    try {
      const res = await uploadAvatar(blob);
      setCam(false);
      setAvatarVer((v) => v + 1);
      const left = res?.remaining_this_month;
      toast.success(
        typeof left === "number"
          ? `تَمّ توليد الأَفتار. مُتَبَقّي ${left} مَحاولة هذا الشَّهر.`
          : "تَمّ تَحديث الأَفتار",
      );
      onSaved && onSaved();
    } catch (e) {
      // Surface the real backend message (quota, AI failure, invalid PNG…)
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "تَعذّر توليد الأَفتار. حاوِل مَرّة أُخرى.");
      throw e; // let the picker switch back to preview state
    } finally {
      setBusy(false);
    }
  };

  const charLeft = 280 - bio.length;
  const avatarSrc = user?.avatar_id ? `${avatarUrl(user.avatar_id)}?v=${avatarVer}` : "";

  return (
    <div id="public-presence" data-testid="public-presence-section">
      <SectionTitle>بَياناتي وَظُهوري</SectionTitle>

      <div className="bg-white border border-[#EAEAEA] rounded-[20px] p-4 space-y-4 shadow-[0_2px_18px_rgba(10,25,47,0.04)]">
        {/* تَوثيق بِبِطاقة النِّقابة */}
        <BarCardStatus user={user} onUpdated={(fresh) => onSaved && onSaved(fresh)} />

        {/* أَفتار + زرّ كاميرا */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-[#F5F5F5] overflow-hidden flex items-center justify-center ring-4 ring-[#D4AF37]/40" data-testid="public-presence-avatar">
            {avatarSrc ? (
              <img src={avatarSrc} alt="أَفتار" className="w-full h-full object-cover" />
            ) : (
              <Camera size={24} className="text-[#5A6470]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <button
              type="button"
              onClick={() => setCam(true)}
              disabled={busy}
              data-testid="open-avatar-camera"
              className="px-4 py-2 rounded-full bg-[#0A192F] text-white text-[13px] font-bold active:scale-95 transition disabled:opacity-50"
            >
              <Camera size={14} weight="bold" className="inline -mt-0.5 ml-1" />
              {avatarSrc ? "تَغيير الأَفتار" : "التِقاط أَفتار"}
            </button>
            <p className="text-[11px] text-[#5A6470] mt-1.5 leading-relaxed">
              🔒 الكاميرا فَقَط — تُحَوَّل بالـ AI إلى أَفتار يُشبِهك دون أن يَكون صورتك الحَقيقيّة
            </p>
          </div>
        </div>

        {/* مفتاح التَّفعيل */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#F0F0F0]">
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-[14px] font-bold text-[#0A192F]">
              <GlobeHemisphereWest size={16} weight="duotone" color="#D4AF37" />
              تَفعيل ظهوري للعُملاء
            </div>
            <p className="text-[11.5px] text-[#5A6470] mt-0.5 leading-relaxed">
              عند التَّفعيل تَظهر في جدول الترتيب + الدليل العامّ
            </p>
          </div>
          <Toggle checked={optIn} onChange={setOptIn} testid="leaderboard-opt-in" />
        </div>

        <Field label="الاسم الكامِل" required hint="يَظهَر في صَفحتك العامّة وفي الدليل">
          <div className="relative">
            <User size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" weight="bold" />
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="مَثَلاً: عَليّ محسن الجابِريّ"
              data-testid="name-input"
              className={`${inputCls} pr-9`}
            />
          </div>
        </Field>

        <Field label="رقم الهاتف" required hint="مَطلوب لِيَتَواصل العُملاء معك عبر WhatsApp">
          <div className="relative">
            <Phone size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" weight="bold" />
            <input
              type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="07XX XXX XXXX"
              data-testid="phone-input" dir="ltr"
              className={`${inputCls} pr-9 text-right`}
            />
          </div>
        </Field>

        <Field label="التَّخصُّص" required>
          <input
            type="text" value={specialty} onChange={(e) => setSpecialty(e.target.value)}
            placeholder="مَثَلاً: قانون مَدنيّ + عُقود تجاريّة" maxLength={120}
            data-testid="specialty-input" className={inputCls}
          />
        </Field>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="المُحافَظة" required>
            <div className="relative">
              <MapPin size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" weight="bold" />
              <select
                value={governorate} onChange={(e) => setGovernorate(e.target.value)}
                data-testid="governorate-input"
                className={`${inputCls} pr-9 appearance-none`}
              >
                <option value="">اختَر...</option>
                {GOVERNORATES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </Field>
          <Field label="القَضاء">
            <input
              type="text" value={district} onChange={(e) => setDistrict(e.target.value)}
              placeholder="مَثَلاً: الكَرّادة، الكاظميّة" maxLength={80}
              data-testid="district-input" className={inputCls}
            />
          </Field>
        </div>

        <Field label="المدينة (يَدويّة — اختياريّة)">
          <input
            type="text" value={city} onChange={(e) => setCity(e.target.value)}
            placeholder="إن أَردت تَخصيص المدينة المُعلَنة" maxLength={80}
            data-testid="city-input" className={inputCls}
          />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[12.5px] font-bold text-[#0A192F]">نُبذة قَصيرة</label>
            <span className={`text-[10.5px] ${charLeft < 30 ? "text-[#B91C1C]" : "text-[#5A6470]"}`}>
              {charLeft} حَرفاً مُتَبَقّياً
            </span>
          </div>
          <textarea
            value={bio} onChange={(e) => setBio(e.target.value.slice(0, 280))}
            placeholder="خِبراتك، شَهاداتك، فلسفَتك المهنيّة... (280 حَرفاً)" rows={3}
            data-testid="bio-input"
            className={`${inputCls} resize-none leading-relaxed`}
          />
        </div>

        <button
          type="button" onClick={save} disabled={busy}
          data-testid="public-presence-save"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#22C55E] text-white text-[14px] font-bold active:scale-95 transition disabled:opacity-50 shadow-[0_4px_14px_rgba(34,197,94,0.30)]"
        >
          <Check size={16} weight="bold" />
          حفظ الإعدادات
        </button>
      </div>

      <CameraAvatarPicker open={cam} onClose={() => setCam(false)} onCaptured={onCaptured} />
    </div>
  );
}
